import { useEffect, useState } from 'react';
import {
    Box,
    Button,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    SelectChangeEvent,
    Skeleton,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ArrowUpward, Cancel, CheckCircle, Clear, OpenInNew } from '@mui/icons-material';

import { CompanyAutocomplete } from '../crm/components/CompanyAutocomplete';
import { Company } from '../crm/crm.dto';

import BoQTableView, { EditMode } from './components/BoQTableView';
import BoQMarkErrorDialog, { BoQMarkErrorDialogResult } from './components/BoQMarkErrorDialog';
import StepperActions from './components/StepperActions';
import { HUBSPOT_PORTAL_ID } from 'src/appConfig';
import { useAppDispatch, useAppSelector } from 'src/redux-app-hooks';
import {
    applyChangesOfSelectedOfferSubmission,
    disqualifySelectedOfferSubmission,
    markOfferSubmissionAssessmentError,
    selectOfferSubmission,
    updateOfferSubmissionItem,
    updateOfferSubmissionTotalDiscount,
    updateOfferSubmissionTotalPrice,
    updateSelectedBidderCompany,
} from './offer-evaluation.slice';
import { OfferSubmissionFileFormat, OfferSubmissionItem } from './dto/offer-submission.dto';
import { admiTenderService } from './admi-tender.service';

const HUBSPOT_CREATE_COMPANY_URL = `https://app-eu1.hubspot.com/object-builder/${HUBSPOT_PORTAL_ID}/0-2/embed?`;

const EDIT_MODE_BY_FILE_FORMAT: Record<OfferSubmissionFileFormat, EditMode> = {
    GAEB: EditMode.READ_ONLY,
    PDF: EditMode.EDITABLE,
    MANUAL: EditMode.EDITABLE_UPDATE_TOTAL_PRICES,
};

export default function OfferEvaluationStepReviewOffers() {
    const { offerSubmissions, selectedOfferSubmission, selectedBidderCompany } = useAppSelector(
        (state) => state.offerEvaluation,
    );
    const dispatch = useAppDispatch();
    const { t } = useTranslation();

    const [dialogOpenedForOrdinal, setDialogOpenedForOrdinal] = useState<string | null>(null);
    const [pdfObjectUrl, setPdfObjectUrl] = useState<string | null>(null);
    const dialogOpenForItem = selectedOfferSubmission?.items.find((item) => item.ordinal === dialogOpenedForOrdinal);
    const someOfferIsNotReviewed = offerSubmissions.some((offerSubmission) => offerSubmission.state === 'NOT_REVIEWED');

    const handleChangeBidder = (event: SelectChangeEvent) => {
        const offerSubmissionId = event.target.value;
        dispatch(selectOfferSubmission(offerSubmissionId));
    };

    const switchToNextOfferSubmission = () => {
        const currentOfferSubmissionIndex = offerSubmissions.findIndex(
            (offerSubmission) => offerSubmission.id === selectedOfferSubmission?.id,
        );
        const nextOfferSubmission = offerSubmissions[(currentOfferSubmissionIndex + 1) % offerSubmissions.length];
        dispatch(selectOfferSubmission(nextOfferSubmission.id));
    };

    const handleApplyChanges = async () => {
        await dispatch(applyChangesOfSelectedOfferSubmission());
        switchToNextOfferSubmission();
    };

    const handleDisqualifyOffer = async () => {
        await dispatch(disqualifySelectedOfferSubmission());
        switchToNextOfferSubmission();
    };

    const handleMarkErrorDialogOpen = (ordinal: string) => {
        setDialogOpenedForOrdinal(ordinal);
    };

    const handleMarkErrorDialogClose = async (result: BoQMarkErrorDialogResult) => {
        if (result.action === 'confirm' && dialogOpenedForOrdinal) {
            if (!selectedOfferSubmission) {
                throw new Error('No offer submission selected');
            }

            await dispatch(
                markOfferSubmissionAssessmentError({
                    offerSubmissionId: selectedOfferSubmission.id,
                    ordinal: dialogOpenedForOrdinal,
                    type: 'TECHNISCHE_PRUEFUNG_T1',
                    reason: result.reason,
                }),
            );
        }

        setDialogOpenedForOrdinal(null);
    };

    const handleItemChange = (item: OfferSubmissionItem) => {
        dispatch(updateOfferSubmissionItem(item));
    };

    const handleTotalPriceChange = (totalPrice: string) => {
        dispatch(updateOfferSubmissionTotalPrice(totalPrice));
    };

    const handleTotalDiscountPercentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        dispatch(updateOfferSubmissionTotalDiscount(event.target.value));
    };

    // Select first offer submission if none is selected
    useEffect(() => {
        if (offerSubmissions.length > 0 && !selectedOfferSubmission) {
            dispatch(selectOfferSubmission(offerSubmissions[0].id));
        }
    }, [dispatch, offerSubmissions, selectedOfferSubmission]);

    // Reset selectedOfferSubmission on unmount
    useEffect(() => {
        return () => {
            dispatch(selectOfferSubmission(null));
        };
    }, [dispatch]);

    // Load PDF blob into object url so it can be displayed within an iframe
    useEffect(() => {
        if (selectedOfferSubmission?.fileFormat !== 'PDF') {
            setPdfObjectUrl(null);
            return;
        }

        let isStale = false;
        let objectUrl: string | null = null;

        admiTenderService.getOfferSubmissionPdfPreview(selectedOfferSubmission.id).then((blob) => {
            if (isStale) {
                return;
            }
            objectUrl = URL.createObjectURL(blob);
            setPdfObjectUrl(objectUrl);
        });

        return () => {
            isStale = true;
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
        };
    }, [selectedOfferSubmission?.id, selectedOfferSubmission?.fileFormat]);

    if (offerSubmissions.length === 0) {
        return <Typography variant="h5">{t('No offer submissions found')}</Typography>;
    }

    return (
        <>
            <Typography variant="h5" gutterBottom>
                {t('Review Offer Submissions')}
            </Typography>
            <Typography variant="body1">
                {t(
                    'The goal of this step is to make sure that the contents of all offer submissions are ingested correctly. It is only about transferring the items, NOT correcting computational mistakes.',
                )}
                <br />
                {t('The correction follows later in the process and is done automatically.')}
            </Typography>

            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <FormControl sx={{ minWidth: 100 }}>
                    <InputLabel>{t('Bidder')}</InputLabel>
                    <Select
                        value={selectedOfferSubmission?.id ?? ''}
                        label={t('Bidder')}
                        onChange={handleChangeBidder}
                        sx={{ minWidth: 400 }}
                    >
                        {offerSubmissions.map((offerSubmission) => (
                            <MenuItem key={offerSubmission.id} value={offerSubmission.id}>
                                {offerSubmission.bidderName} ({offerSubmission.fileFormat})
                                {offerSubmission.state === 'REVIEWED' && (
                                    <CheckCircle sx={{ ml: 1, verticalAlign: 'middle' }} color="action" />
                                )}
                                {offerSubmission.state === 'DISQUALIFIED' && (
                                    <Cancel sx={{ ml: 1, verticalAlign: 'middle' }} color="error" />
                                )}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Stack direction="row" sx={{ gap: 1 }}>
                    <Button
                        variant="contained"
                        color="secondary"
                        startIcon={<ArrowUpward />}
                        onClick={handleApplyChanges}
                    >
                        {t('Apply changes')}
                    </Button>
                    <Button variant="contained" color="error" startIcon={<Clear />} onClick={handleDisqualifyOffer}>
                        {t('Disqualify offer')}
                    </Button>
                </Stack>
            </Box>

            <Paper sx={{ mt: 2, p: 2 }}>
                <Stack direction="row" sx={{ gap: 1 }}>
                    <CompanyAutocomplete
                        value={selectedBidderCompany}
                        size="small"
                        disableClearable
                        onSelectCompany={(company: Company | null) => {
                            if (company) dispatch(updateSelectedBidderCompany(company));
                        }}
                    />
                    <Button
                        variant="outlined"
                        startIcon={<OpenInNew />}
                        component="a"
                        href={HUBSPOT_CREATE_COMPANY_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {t('Create new')}
                    </Button>

                    <TextField
                        type="number"
                        label={t('Discount according to Formblatt 213 (%)')}
                        size="small"
                        value={selectedOfferSubmission?.totalDiscountPercent ?? ''}
                        onChange={handleTotalDiscountPercentChange}
                        slotProps={{ htmlInput: { min: 0 } }}
                        sx={{ ml: 2, width: '15rem' }}
                    />
                </Stack>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {t('The following fields are required for companies show up here')}: name, address, zip, city
                </Typography>

                {selectedOfferSubmission && (
                    <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                        {selectedOfferSubmission.fileFormat === 'PDF' &&
                            (!pdfObjectUrl ? (
                                <Skeleton variant="rectangular" height="80vh" sx={{ flex: 1 }} />
                            ) : (
                                <Paper sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                                    <Box
                                        component="embed"
                                        type="application/pdf"
                                        src={pdfObjectUrl}
                                        sx={{ width: '100%', height: '80vh', border: 0 }}
                                    />
                                </Paper>
                            ))}

                        <Box
                            sx={{
                                flex: 1,
                                minWidth: 0,
                                overflow: 'auto',
                                height: selectedOfferSubmission.fileFormat === 'PDF' ? '80vh' : 'auto',
                            }}
                        >
                            <BoQTableView
                                totalPrice={selectedOfferSubmission.totalPrice}
                                items={selectedOfferSubmission.items}
                                editMode={EDIT_MODE_BY_FILE_FORMAT[selectedOfferSubmission.fileFormat]}
                                onMarkError={handleMarkErrorDialogOpen}
                                onItemChange={handleItemChange}
                                onTotalPriceChange={handleTotalPriceChange}
                            />
                        </Box>
                    </Box>
                )}
            </Paper>

            <BoQMarkErrorDialog
                title="Technische Prüfung"
                description="Beschreibe im Folgenden, warum die technische Prüfung ungültig ist. Dies kann z.B. eine Abweichung
                    vom erlaubten Toleranzbereich sein."
                initialReason={dialogOpenForItem?.assessmentFailReasons['TECHNISCHE_PRUEFUNG_T1'] ?? ''}
                open={Boolean(dialogOpenedForOrdinal)}
                onClose={handleMarkErrorDialogClose}
            />

            <StepperActions disabledNext={someOfferIsNotReviewed} />
        </>
    );
}
